import React, { createContext, useReducer, FunctionComponent } from 'react'
import MovieReducer, { MovieState, initialState, MovieActionType } from '@reducers/MovieReducer'
import API from '@api/client'
import { ResponseMeta } from '@models/Common'
import { Movie, MovieEntry, movieGenres } from '@models/Movie'

export type MovieContext = MovieState & {
  searchMovie(term: string, language: string): void
  selectMovie(id?: MovieEntry): void
  selectGenre(genre?: string): void
}

export const initialMovieContext: MovieContext = {
  ...initialState,
  searchMovie: () => {},
  selectMovie: () => {},
  selectGenre: () => {},
}

export const movieContext = createContext<MovieContext>(initialMovieContext)

export const genreMap: Record<string, string[]> = {
  ACTION: [
    'Action',
    'Action Super Hero',
    'Bad Cop',
    'Buddy Cop',
    'Girls with Guns',
    'Heroic Bloodshed',
    'Military Film',
    'Secret Service',
  ],
  ADVENTURE: ['Adventure', 'Cloak-And-Dagger Film', 'Knight\'s Tale', 'Pirate Film', 'Robinsonade', 'Treasure Hunting'],
  ANIMATION: ['Animation', 'Anime', 'Cartoon Anime', 'Classical Animation', 'Computer Animation', 'Stop Motion'],
  COMEDY: [
    'Comedy',
    'Action Comedy',
    'Black Comedy',
    'Comedy Of Mistaken Identity',
    'Alter Ego',
    'Crime Comedy',
    'Criminal Comedy Film',
    'Dramedy',
    'Erotic Comedy Film',
    'Family Comedy',
    'Farce',
    'Gross-Out Film',
    'Grotesque',
    'Horror comedy',
    'Parody',
    'Romantic Comedy',
    'Satire',
    'Political Satire',
    'Anarchic Comedy Film',
    'Satire On Media',
    'Satire On War',
    'Screwball Comedy Film',
    'Sitcom',
    'Slapstick Comedy Film',
    'Teen Comedy Film',
    'High School Comedy Film',
    'Tragicomedy',
  ],
  CRIME: [
    'Crime',
    'Detective Film',
    'Film Noir',
    'Gangster Film',
    'Heist Film',
    'Caper Movie',
    'Mafia Film',
    'Yakuza Film',
    'Police Film',
    'Italian Crime Film',
  ],
  'DISASTER FILM': ['Disaster Film', 'Accident', 'Natural Disaster', 'Terrorism'],
  DOCUMENTARY: [
    'Documentary',
    'Conspiracy theory film',
    'Cultural Film',
    'Educational Film',
    'Essay Film',
    'Event Documentary',
    'Investigative Documentary',
    'Long Term Documentary',
    'Making-Of Documentary',
    'Nature Documentary',
    'Parascientific Documentary',
    'Political Documentary',
    'Scientific Documentary',
    'Social Study',
    'Staged Documentary',
    'Dokufiction',
    'Drama Documentary',
    'Horror',
    'Haunted House Film',
    'Monster Film',
    'Animalhorror',
    'Cannibal-Movie',
    'Extraterrestrial Monsters',
    'Ghost-Movie',
    'Japanese Monster Movie',
    'J-Horror',
    'Vampiremovie',
    'Werewolve-Movie',
    'Zombiefilm',
    'Occult Horror Film',
    'Exorcism Film',
    'Obsession',
    'Satanism',
    'Slasher Film',
    'Teen Horror Film',
    'Torture Porn',
    'Splatter Film / Gore Film',
    'Mockumentary',
    'Mondo',
    'Reality Show',
    'Travel documentary',
  ],
  DRAMA: [
    'Drama',
    'Biopic',
    'Blaxploitation',
    'Buddy Film',
    'Coming Of Age',
    'Destiny',
    'Family Drama',
    'Family Saga',
    'Marital Tragedy',
    'Justice Drama',
    'Courtroom Drama',
    'Political Drama',
    'Refugee Drama',
    'Psychodrama',
    'Regional film',
    'Mountain Film',
    'Romantic Drama',
    'Melodrama',
    'Romance Film',
    'Social Drama',
    'Coming Out',
  ],
  EASTERN: ['Eastern', 'Martial Arts', 'Ninja Film', 'Samurai Film', 'Wuxia Film'],
  'EROTIC-FILM': [
    'Erotic Film',
    'Lederhosen Film',
    'Nudie Film',
    'Sex Education',
    'Sex Report Film',
    'Sex Film',
    'Sexploitation Film',
  ],
  'EXPLOITATION FILM': ['Exploitation Film'],
  FANTASY: [
    'Fantasy',
    'Bangsian Fantasy',
    'Contemporary Fantasy',
    'Fairy Tale Film',
    'High Fantasy',
    'Low Fantasy',
    'Historical Fantasy',
    'Sword And Sorcery',
  ],
  'FOUND-FOOTAGE': ['Found Footage'],
  'HISTORICAL-FILM': [
    'Historical Film',
    'Anti-War Film',
    'Monumental Film',
    'Bible Film',
    'Swords and Sandals',
    'War Film',
  ],
  MUSICALS: ['Musicals', 'Concert film', 'Dance Film', 'Musical'],
  'ROAD MOVIE': ['Road Movie', 'End Times Road Movie', 'Motorcycle Film', 'Racing Film', 'Truck Film'],
  'SCIENCE FICTION': [
    'Science Fiction',
    'Apocalypse & Post-Apocalypse',
    'Cyberpunk',
    'Hard SF',
    'Retro SF',
    'Space Opera',
    'Time Travel',
    'Utopia & Dystopia',
  ],
  'SPORT-FILM': ['Sport Film'],
  THRILLER: [
    'Thriller',
    'Conspiracy Thriller',
    'Erotic Thriller',
    'Mystery Thriller',
    'Political Thriller',
    'Psychological Thriller',
  ],
  WESTERN: ['Western', 'Classical Western', 'Late Western', 'Ostern', 'Spaghetti Western'],
}

const mapGenre = (name: string): string => {
  const lowerName = name.toLowerCase()
  for (const k in genreMap) {
    const foundGenre = genreMap[k].find((g) => g === lowerName)
    if (foundGenre) {
      return k
    }
  }
  return ''
}

const getMovieGenre = (movie: Movie) => {
  if (movie?.genreIds?.length) {
    const genre = movieGenres.find((g) => g.id === movie.genreIds[0])
    if (genre) {
      const mappedGenre = mapGenre(genre.name)
      if (mappedGenre) {
        return mappedGenre
      }
      return genre.name
    }
  }
  return 'unknown'
}

const basePostImagePath = 'https://image.tmdb.org/t/p/w400'

export default function WithMovie<P extends {}>(WrappedComponent: FunctionComponent<P>): FunctionComponent<P> {
  const ComponentWithMovie: FunctionComponent<P> = (props) => {
    const [state, dispatch] = useReducer(MovieReducer, initialState)

    const searchMovies = (term: string, language: string) => {
      dispatch({ type: MovieActionType.SearchMoviesPending, payload: term })
      if (!term) {
        dispatch({ type: MovieActionType.SearchMoviesSuccess, payload: [] })
      } else {
        API.searchMovies(term, language)
          .then((searchResults) => {
            const movieEntries = searchResults?.data?.results
              .filter((m) => {
                //skipping non-latin titles
                const nonLatin = /[^\u0020-\u007F\u00A0-\u024F\u1E00-\u1EFF]/
                if (nonLatin.test(m.title)) return false
                return true
              })
              .map((m) => {
                return {
                  id: m.id.toString(),
                  term,
                  title: m.title,
                  genre: getMovieGenre(m),
                  artworkUrl: `${basePostImagePath}${m.posterPath}`,
                }
              })

            dispatch({ type: MovieActionType.SearchMoviesSuccess, payload: movieEntries })
          })
          .catch((error: ResponseMeta) => {
            dispatch({ type: MovieActionType.SearchMoviesFailure, payload: error.message })
          })
      }
    }

    const selectMovie = (movie?: MovieEntry) => {
      dispatch({ type: MovieActionType.SelectMovie, payload: movie })
    }

    const selectGenre = (genre?: string) => {
      dispatch({ type: MovieActionType.SelectGenre, payload: genre })
    }

    return (
      <movieContext.Provider
        value={{
          ...state,
          searchMovie: searchMovies,
          selectMovie,
          selectGenre,
        }}>
        <WrappedComponent {...props} />
      </movieContext.Provider>
    )
  }
  return ComponentWithMovie
}
